import { Alert, Form, Spinner, Table } from "react-bootstrap";
import { useSelector } from "react-redux";

/**
 * Componente para mostrar la evaluación tipo Likert de competencias o valores.
 * Permite seleccionar métricas mediante radio buttons y muestra los datos en una tabla.
 */
const LikertEvaluation = ({
    data,        // Array de competencias o valores a evaluar
    onSubmit,    // Handler para cambio de métrica
    readOnly = false, // Si es solo lectura, muestra "X" en vez de radio
    disabled = false, // Deshabilita los inputs
    type         // Tipo de evaluación: "Competencias" o "Valores"
}) => {
    // Obtiene métricas y estado de carga desde Redux
    const {
        loading: loadingObjectiveMetric,
        objectiveMetrics,
        objectiveMetricsLength,
        objectiveMetricError
    } = useSelector((store) => store.objectiveMetric);

    // Ordenar métricas por escala (menor a mayor)
    const sortedObjectiveMetrics = [...objectiveMetrics].sort((a, b) => {
        // Si escala es numérica, compara como número
        const escalaA = isNaN(Number(a.escala)) ? a.escala : Number(a.escala);
        const escalaB = isNaN(Number(b.escala)) ? b.escala : Number(b.escala);
        if (escalaA < escalaB) return -1;
        if (escalaA > escalaB) return 1;
        return 0;
    });

    // Datos de pilares según el tipo
    const pillarsData = type === "Competencias" ? data?.competencias_pilares : data?.valores_pilares;

    /* El objeto de respuesta guardado en objectiveMetrics está dividido en data, count y competencias_pilares o valores_pilares.
       - competencias_pilares o valores_pilares: Contiene los pilares y cada pilar contiene otro arreglo de objetos con las respuestas que pertenecen a dicho pilar.
       - count: Cantidad total de preguntas para el formulario de valores o competencias.
       - data: arreglo con todas las preguntas de la evaluación y su respuesta (id de la métrica seleccionada).        
    */

    /*
        El formulario se está desplegando por medio de competencias_pilares o valores_pilares, iterando sobre cada pilar y luego sobre cada pregunta dentro del pilar.
        Dado que competencias_pilares o valores_pilares no contienen la respuesta directamente, se crea esta función para buscar en el arreglo data la respuesta correspondiente a cada pregunta.
        @param {Object} pillar_question - Objeto de la pregunta dentro del pilar actual.
    */
    const getQuestionAnswer = (pillar_question) => {
        let answer = data?.data?.find( dataQuestion => (type === "Competencias" ? dataQuestion?.competencia_data : dataQuestion?.valor_data).id === pillar_question.id );
        return answer;
    }

    /*
        Similar a la función anterior, esta función obtiene el índice de la respuesta en el arreglo data.
        Esto es útil para pasar el índice correcto al handler onSubmit cuando se cambia la métrica.
        @param {Object} pillar_question - Objeto de la pregunta dentro del pilar actual.
    */
    const getAnswerIndex = (pillar_question) => {
        let answerIndex = data?.data?.findIndex( dataQuestion => (type === "Competencias" ? dataQuestion?.competencia_data : dataQuestion?.valor_data).id === pillar_question.id );
        return answerIndex;
    }


    return (
        <div className="my-3">
            {/* Spinner de carga */}
            {loadingObjectiveMetric ? (
                <div className="text-center">
                    <Spinner animation="border" role="status" size="sm" />
                </div>
            ) : (type === "Competencias" ? data?.competencias_pilares?.length > 0 : data?.valores_pilares?.length > 0) ? (
                <Form>
                    {/* Alerta de error si existe */}
                    <Alert variant="danger" show={objectiveMetricError}>
                        {objectiveMetricError}
                    </Alert>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th rowSpan={2} className="text-center">
                                    {type === "Competencias" ? "Competencias" : "Valores"}
                                </th>
                                <th rowSpan={2} className="text-center">Comportamiento(s)</th>
                                {/* Encabezados de métricas */}
                                {sortedObjectiveMetrics.map((metric) => (
                                    <th key={metric.id} className="text-center">
                                        {metric.nombre}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                {/* Escalas de métricas */}
                                {sortedObjectiveMetrics.map((metric) => (
                                    <th key={metric.id} className="text-center">
                                        {metric.escala}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {/* Filas de datos */}
                            {pillarsData?.map((pilarItem, pilarItemIndex) => {
                                // Selecciona las preguntas del pilar según el tipo
                                const questions = type === "Competencias" ? pilarItem?.competencias : pilarItem?.valores;
                                return questions?.map((questionItem, questionIndex) => {
                                    let answer = getQuestionAnswer(questionItem);
                                    let answerIndex = getAnswerIndex(questionItem);
                                    return (
                                        <tr key={questionItem?.id ?? `${pilarItemIndex}-${questionIndex}`}>
                                            {questionIndex === 0 && (
                                                <td rowSpan={questions?.length}>
                                                    {pilarItem.pilar_nombre}
                                            </td>
                                        )}
                                        <td>
                                            {questionItem?.nombre}
                                        </td>
                                        {/* Radios o "X" según modo de lectura */}
                                        {sortedObjectiveMetrics.map((metric) => (
                                            <td key={metric.id} className="text-center">
                                                {readOnly ? (
                                                    answer.metrica === metric.id ? "X" : ""
                                                ) : (
                                                    <Form.Check
                                                        type="radio"
                                                        name={`question_${questionItem.id}`}
                                                        value={metric.id}
                                                        checked={ answer.metrica === metric.id}
                                                        onChange={() => {
                                                            onSubmit(answer, metric.id, answerIndex);
                                                        }}
                                                        disabled={disabled}
                                                    />
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                )});
                            })}
                            
                        </tbody>
                    </Table>
                </Form>
            ) : (
                // Mensaje si no hay datos
                <div className="text-center">
                    <p>No hay datos disponibles para mostrar.</p>
                </div>
            )}
        </div>

    )
}

export default LikertEvaluation;